import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { specsEditorActions } from "../../common/slices/specsEditor/slice";
import { SpecsModel } from "./specsModel/SpecsModel";
import { makeLayerRuntimeKey } from "./specsEditorRuntimeKeys";
import type {
  SpecsEditorIdentity,
  SpecsEditorSession,
  SpecsEditorNav,
  SpecsLayer,
} from "./types";

type UseSpecsEditorLayersArgs = {
  currentVersion: ReturnType<SpecsModel["getVersion"]> | null;
  identity: SpecsEditorIdentity;
  selectedLayer: SpecsLayer | null;
  selectedNav: SpecsEditorNav;
  sessionLayers: SpecsLayer[];
};

function getLayerScopeFromNav(nav: SpecsEditorNav) {
  if (
    nav.kind === "layer" ||
    nav.kind === "element" ||
    nav.kind === "layerSpecs" ||
    nav.kind === "positions"
  ) {
    if (nav.part && nav.element && nav.style) {
      return {
        partName: nav.part,
        elementName: nav.element,
        styleName: nav.style,
      };
    }
  }

  return null;
}

export function useSpecsEditorLayers({
  currentVersion,
  identity,
  selectedLayer,
  selectedNav,
  sessionLayers,
}: UseSpecsEditorLayersArgs) {
  const dispatch = useDispatch();
  const prevLayerIdsRef = useRef<string>(
    sessionLayers.map((layer) => layer.id).join(",")
  );

  useEffect(() => {
    const layerIds = sessionLayers.map((layer) => layer.id).join(",");
    const layersChanged = layerIds !== prevLayerIdsRef.current;
    prevLayerIdsRef.current = layerIds;

    if (sessionLayers.length === 0) {
      return;
    }

    if (layersChanged && selectedNav.kind === "layer" && !selectedLayer) {
      const firstVisibleLayer =
        sessionLayers.find((layer) => layer.visible) ?? sessionLayers[0];
      dispatch(specsEditorActions.selectLayer(firstVisibleLayer.id));
    }
  }, [dispatch, selectedLayer, selectedNav.kind, sessionLayers]);

  const getBoundModelLayerBinding = useCallback(
    (layerId: string) => {
      const layer = sessionLayers.find((item) => item.id === layerId);
      if (!layer?.modelLayerPath) {
        return null;
      }

      return (
        currentVersion?.getLayer({
          partName: layer.modelLayerPath.partName,
          elementName: layer.modelLayerPath.elementName,
          styleName: layer.modelLayerPath.styleName,
          layerName: layer.modelLayerPath.layerName,
        }) ?? null
      );
    },
    [currentVersion, sessionLayers]
  );

  const addLayer = (name?: string) => {
    const scope = getLayerScopeFromNav(selectedNav);
    if (currentVersion && scope) {
      const elementBinding = currentVersion.getElement(
        scope.elementName,
        scope.partName
      );
      const style = elementBinding?.element.getStyle(scope.styleName);
      if (style) {
        const baseName = name?.trim() || `Layer ${style.layers.length + 1}`;
        let layerName = baseName;
        let suffix = 2;
        while (style.layers.some((layer) => layer.name === layerName)) {
          layerName = `${baseName} ${suffix}`;
          suffix += 1;
        }

        const result = currentVersion.addLayer({
          ...scope,
          layerName,
        });
        if (result) {
          const layerId = makeLayerRuntimeKey({
            ...scope,
            layerName,
          });
          dispatch(
            specsEditorActions.setNav({
              kind: "layer",
              product: identity.productName,
              version: identity.productVersion,
              part: scope.partName,
              element: scope.elementName,
              style: scope.styleName,
              layerId,
            })
          );
          return;
        }
      }
    }

    dispatch(specsEditorActions.addLayer(name));
  };

  return {
    addLayer,
    ensureLayerHighlight: (layerId: string) =>
      dispatch(specsEditorActions.ensureLayerHighlight(layerId)),
    toggleLayerVisibility: (layerId: string) => {
      const binding = getBoundModelLayerBinding(layerId);
      if (binding) {
        binding.layer.displayable = !binding.layer.displayable;
        return;
      }
      dispatch(specsEditorActions.toggleLayerVisibility(layerId));
    },
    updateLayerColor: (layerId: string, color: string) => {
      const layer = sessionLayers.find((item) => item.id === layerId);
      dispatch(specsEditorActions.updateLayerColor({ layerId, color, layer }));
    },
    updateLayerZIndex: (layerId: string, zIndex: number) => {
      const binding = getBoundModelLayerBinding(layerId);
      if (binding) {
        for (const side of binding.layer.sides) {
          side.zIndex = zIndex;
        }
        return;
      }
      dispatch(specsEditorActions.updateLayerZIndex({ layerId, zIndex }));
    },
    removeLayer: (layerId: string) => {
      const binding = getBoundModelLayerBinding(layerId);
      if (binding) {
        const layerIndex = binding.style.layers.indexOf(binding.layer);
        if (layerIndex !== -1) {
          binding.style.layers.splice(layerIndex, 1);
          if (binding.style.defaultLayer === binding.layer.name) {
            binding.style.defaultLayer = binding.style.layers[0]?.name ?? "";
          }
          if (
            (selectedNav.kind === "layer" ||
              selectedNav.kind === "positions") &&
            selectedNav.layerId === layerId
          ) {
            dispatch(
              specsEditorActions.setNav({
                kind: "element",
                product: identity.productName,
                version: identity.productVersion,
                part: binding.part.name,
                element: binding.element.name,
                style: binding.style.name,
              })
            );
          }
          return;
        }
      }
      dispatch(specsEditorActions.removeLayer(layerId));
    },
    updateLayer: (layerId: string, patch: { name?: string }) => {
      const nextName = patch.name;
      if (nextName !== undefined) {
        const binding = getBoundModelLayerBinding(layerId);
        if (binding) {
          binding.layer.layerLabel = nextName;
          return;
        }
      }
      dispatch(specsEditorActions.updateLayer({ layerId, patch }));
    },
    duplicateLayer: (layerId: string) =>
      dispatch(specsEditorActions.duplicateLayer(layerId)),
    moveLayer: (layerId: string, direction: "up" | "down") => {
      const binding = getBoundModelLayerBinding(layerId);
      if (binding) {
        const layerIndex = binding.style.layers.indexOf(binding.layer);
        const nextIndex = direction === "up" ? layerIndex - 1 : layerIndex + 1;
        if (layerIndex !== -1 && nextIndex >= 0) {
          if (nextIndex < binding.style.layers.length) {
            binding.style.layers.splice(layerIndex, 1);
            binding.style.layers.splice(nextIndex, 0, binding.layer);
            return;
          }
        }
      }
      dispatch(specsEditorActions.moveLayer({ layerId, direction }));
    },
    setReferenceImageUrl: (url: string | null) =>
      dispatch(specsEditorActions.setReferenceImageUrl(url)),
  };
}
